"use strict";
/**
 * TabSyncService.ts
 * Meci Multi-Tab Session Synchronization & Non-blocking Echo Warning
 *
 * Responsibilities:
 * 1. Synchronizes microphone mute & deafen state across multiple tabs in real-time.
 * 2. Detects if the same user is connected to voice in multiple tabs simultaneously.
 * 3. Shows a subtle, dismissible warning pill instead of blocking the screen.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.tabSyncService = void 0;
class TabSyncService {
    constructor() {
        this.userId = null;
        this.username = '';
        this.channel = null;
        this.isVoiceActive = false;
        this.otherVoiceTabs = new Map(); // tabId -> lastSeenTimestamp
        this.voiceHeartbeatInterval = null;
        this.listeners = new Set();
        this.isElectron = false;
        this.isDismissed = false;
        this.handleUnload = () => {
            if (this.isVoiceActive) {
                this.postMessage({
                    type: 'VOICE_LEAVE',
                    tabId: this.tabId,
                    userId: this.userId || '',
                    timestamp: Date.now(),
                });
            }
        };
        this.tabId = 'tab_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
        this.isElectron = typeof window !== 'undefined' && !!window.require;
    }
    init(userId, username) {
        if (this.isElectron || typeof window === 'undefined')
            return;
        const uid = String(userId);
        if (!uid)
            return;
        if (this.userId === uid && this.channel) {
            if (username)
                this.username = username;
            return;
        }
        this.cleanup();
        this.userId = uid;
        this.username = username || 'Kullanıcı';
        try {
            this.channel = new BroadcastChannel(`meci_tab_sync_${this.userId}`);
            this.channel.onmessage = (event) => this.handleMessage(event.data);
        }
        catch (e) {
            console.warn('[TabSyncService] BroadcastChannel not supported');
        }
        window.addEventListener('beforeunload', this.handleUnload);
        // Prune stale voice tabs every 3 seconds
        this.voiceHeartbeatInterval = setInterval(() => {
            const now = Date.now();
            let changed = false;
            this.otherVoiceTabs.forEach((timestamp, tid) => {
                if (now - timestamp > 4500) {
                    this.otherVoiceTabs.delete(tid);
                    changed = true;
                }
            });
            if (this.isVoiceActive) {
                this.broadcastVoiceHeartbeat();
            }
            if (changed) {
                this.notify();
            }
        }, 2000);
    }
    /**
     * Broadcast mute state to other tabs so closing/opening mic syncs everywhere.
     */
    broadcastMute(muted) {
        this.postMessage({
            type: 'MUTE_SYNC',
            tabId: this.tabId,
            userId: this.userId || '',
            payload: { muted },
            timestamp: Date.now(),
        });
    }
    /**
     * Broadcast deafen state to other tabs.
     */
    broadcastDeafen(deafened) {
        this.postMessage({
            type: 'DEAFEN_SYNC',
            tabId: this.tabId,
            userId: this.userId || '',
            payload: { deafened },
            timestamp: Date.now(),
        });
    }
    /**
     * Update whether this tab is connected to a voice channel.
     */
    setVoiceActive(active) {
        this.isVoiceActive = active;
        if (active) {
            this.isDismissed = false; // Reset dismiss when joining voice
            this.broadcastVoiceHeartbeat();
        }
        else {
            this.postMessage({
                type: 'VOICE_LEAVE',
                tabId: this.tabId,
                userId: this.userId || '',
                timestamp: Date.now(),
            });
        }
        this.notify();
    }
    dismissWarning() {
        this.isDismissed = true;
        this.notify();
    }
    broadcastVoiceHeartbeat() {
        this.postMessage({
            type: 'VOICE_HEARTBEAT',
            tabId: this.tabId,
            userId: this.userId || '',
            timestamp: Date.now(),
        });
    }
    handleMessage(msg) {
        if (!msg || msg.userId !== this.userId || msg.tabId === this.tabId)
            return;
        switch (msg.type) {
            case 'MUTE_SYNC':
                try {
                    const { useStore } = require('../store/useStore');
                    const state = useStore.getState();
                    if (typeof msg.payload?.muted === 'boolean' && state.isMuted !== msg.payload.muted) {
                        console.log(`[TabSync] Synchronizing mute state from other tab: ${msg.payload.muted}`);
                        state.setMuted(msg.payload.muted);
                    }
                }
                catch { }
                break;
            case 'DEAFEN_SYNC':
                try {
                    const { useStore } = require('../store/useStore');
                    const state = useStore.getState();
                    if (typeof msg.payload?.deafened === 'boolean' && state.isDeafened !== msg.payload.deafened) {
                        console.log(`[TabSync] Synchronizing deafen state from other tab: ${msg.payload.deafened}`);
                        state.setDeafened(msg.payload.deafened);
                    }
                }
                catch { }
                break;
            case 'VOICE_HEARTBEAT':
                this.otherVoiceTabs.set(msg.tabId, Date.now());
                this.notify();
                break;
            case 'VOICE_LEAVE':
                this.otherVoiceTabs.delete(msg.tabId);
                this.notify();
                break;
        }
    }
    postMessage(msg) {
        if (this.channel) {
            try {
                this.channel.postMessage(msg);
            }
            catch { }
        }
    }
    subscribe(listener) {
        this.listeners.add(listener);
        listener(this.hasEchoWarning(), this.otherVoiceTabs.size + (this.isVoiceActive ? 1 : 0));
        return () => this.listeners.delete(listener);
    }
    hasEchoWarning() {
        if (this.isDismissed)
            return false;
        // Warning only triggers if THIS tab is in voice AND AT LEAST ONE other tab is also in voice!
        return this.isVoiceActive && this.otherVoiceTabs.size > 0;
    }
    notify() {
        const warning = this.hasEchoWarning();
        const count = this.otherVoiceTabs.size + (this.isVoiceActive ? 1 : 0);
        this.listeners.forEach((l) => l(warning, count));
    }
    cleanup() {
        if (this.voiceHeartbeatInterval) {
            clearInterval(this.voiceHeartbeatInterval);
            this.voiceHeartbeatInterval = null;
        }
        if (this.channel) {
            try {
                this.channel.close();
            }
            catch { }
            this.channel = null;
        }
        if (typeof window !== 'undefined') {
            window.removeEventListener('beforeunload', this.handleUnload);
        }
        this.userId = null;
        this.otherVoiceTabs.clear();
        this.isVoiceActive = false;
    }
}
exports.tabSyncService = new TabSyncService();
